import React, { type FC, type PropsWithChildren } from 'react';

import { withImage } from '@lucid/hooks';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import Text from '../text/text';
import Heading from '../heading/heading';
import Container from '../container/container';
import ImageWithPlaceholder from '../image/image-with-placeholder';
import VisibleVideo from '../video/visible-video/visible-video';
import CtaWithSSO from '../cta/cta-with-sso/cta-with-sso';

// TODO: can we extend Directus Hero_Section here?
export interface HighlightedHeroProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The button CTA link and text. */
  link?: Lucid.Directus.Generated.Links;
  /** The heading text. */
  header: string;
  /** An image to be placed next to the text. */
  image?: Lucid.Directus.Generated.Directus_Files;
  /** Should the media and content swap sides */
  shouldSwapPositions?: boolean;
  /** The subheader text displayed under the heading text. */
  subheader: string;
  /** Aspect ratio of the media */
  mediaRatio?: '4:3' | '16:9';
}

const HighlightedHero: FC<PropsWithChildren<HighlightedHeroProps>> = ({
  className,
  link,
  header,
  image,
  subheader,
  mediaRatio = '16:9',
  ...rest
}) => (
  <Container>
    <Box
      {...rest}
      css={theme => ({
        label: 'highlightedHero',
        padding: `${theme.space.s64}px 0 ${theme.space.s16}px`,
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        [theme.breakpoints.bp1024.min]: {
          padding: `${theme.space.s64}px 0`,
        },
        [theme.breakpoints.bp1024.min]: {
          flexDirection: 'row',
          justifyContent: 'space-between',
        },
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      <Box
        css={theme => ({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          width: '100%',
          maxWidth: '580px',
          justifyContent: 'space-between',
          [theme.breakpoints.bp1024.min]: {
            maxWidth: '480px',
            paddingRight: theme.space.s40,
            alignItems: 'start',
          },
        })}
      >
        <Heading
          as="h1"
          css={theme => ({
            textAlign: 'center',
            [theme.breakpoints.bp1024.min]: {
              textAlign: 'left',
              marginRight: theme.space.s24,
              paddingRight: theme.space.s24,
            },
          })}
          highlightTextColor="red.60"
          text={header}
          variant="hero4XL400"
        />
        <Text
          css={theme => ({
            textAlign: 'center',
            [theme.breakpoints.bp1024.min]: {
              textAlign: 'left',
            },
          })}
          dataElementPageLocation="hero"
          id="heroLinkDescription"
          variant="paragraphM400"
        >
          {subheader}
        </Text>
        <CtaWithSSO
          aria-describedby="heroLinkDescription"
          css={theme => ({
            margin: '0 auto',
            [theme.breakpoints.bp1024.min]: {
              margin: 'initial',
            },
          })}
          linkText={link?.default_link_text}
          linkUrl={link?.final_url}
        />
      </Box>
      {image && (
        <Box
          css={theme => ({
            width: '100%',
            maxWidth: '860px',
            marginTop: theme.space.s40,
            [theme.breakpoints.bp1024.min]: {
              marginTop: 0,
            },
          })}
        >
          {image?.filename_disk?.includes('.mp4') ? (
            <VisibleVideo shouldLoop description={image?.translations?.[0]?.alt} src={withImage(image)} />
          ) : (
            <ImageWithPlaceholder alt="" aspectRatio={mediaRatio} src={withImage(image)} />
          )}
        </Box>
      )}
    </Box>
  </Container>
);

export { HighlightedHero };
export default HighlightedHero;
